import type { Category, Todo } from "../../domain/entities.ts";
import type { AppView } from "../../app/shell-view.ts";

export interface TaskVisibilityContext {
  view: AppView;
  categoryId: string | null;
  activeCycleId: string | null;
  userId: string;
  today: string;
  showCompleted: boolean;
  myDayTaskIds: ReadonlySet<string>;
  conflicts: ReadonlyMap<string, readonly string[]>;
  searchMatches(todo: Todo): boolean;
  advancedMatches(todo: Todo): boolean;
  archived(todo: Todo): boolean;
}

export interface BoardTaskVisibilityContext {
  categoryId: string | null;
  inactiveMatches(todo: Todo): boolean;
  searchMatches(todo: Todo): boolean;
}

export function taskIsArchived(todo: Todo, categories: readonly Category[]): boolean {
  return !!todo.archivedAt || !!todo.categoryId && !!categories.find(category => category.id === todo.categoryId)?.archivedAt;
}

export function taskVisibleOnBoard(todo: Todo, context: BoardTaskVisibilityContext): boolean {
  return !todo.deleted && !todo.archivedAt && todo.categoryId === context.categoryId && context.inactiveMatches(todo) && context.searchMatches(todo);
}

export function taskVisibleInView(todo: Todo, context: TaskVisibilityContext): boolean {
  if (!context.searchMatches(todo)) return false;
  if (context.categoryId && todo.categoryId !== context.categoryId) return false;
  if (context.view === "trash") return todo.deleted;
  if (todo.deleted) return false;
  if (context.view === "archived") return context.archived(todo);
  if (context.archived(todo)) return false;
  if (context.view === "calendar") return true;
  if (context.view === "upcoming") return !todo.completed && (todo.startDate || todo.dueDate || "") > context.today;
  if (context.view === "scheduled") return !todo.completed && !!(todo.startDate || todo.startAt || todo.dueDate);
  if (context.view === "backlog") return !todo.cycleId && !todo.completed;
  if (context.view === "cycle") return !!context.activeCycleId && todo.cycleId === context.activeCycleId;
  if (context.view === "custom") return context.advancedMatches(todo);
  if (context.view === "assigned") return (todo.assignedTo || []).includes(context.userId);
  if (context.view === "conflicts") return (context.conflicts.get(todo.id)?.length || 0) > 0;
  if (context.view === "completed") return todo.completed;
  if (context.view === "myday") return context.myDayTaskIds.has(todo.id);
  if (context.view === "today") return todo.dueDate === context.today;
  return context.showCompleted || !todo.completed;
}
